import { tw } from "@aet/tailwind/µ"
import { css } from "@emotion/css"
import { Card, Tab, Tabs } from "@heroui/react"
import { useLingui } from "@lingui/react/macro"
import JSON5 from "json5"
import { Fragment, Suspense, useEffect, useMemo, useState } from "react"
import { Button } from "react-aria-components"
import { ErrorBoundary } from "react-error-boundary"

import { KVBox, KVTable } from "#/components/KVTable"

import { ErrorMessage } from "../components/ErrorMessage"
import { Loading } from "../components/Loading"
import type { PostData } from "../har"
import { isJSON } from "../mime"
import { hasSFLetterSpacingBug } from "../utils"

import { JSONView } from "./Body/JSONView"
import { RawEditor } from "./Body/Raw"

enum JSONPostDataTab {
  JSON = "JSON",
  GraphQL = "GraphQL",
  Raw = "Raw",
}
enum FormPostDataTab {
  JSON = "Form Data",
  Raw = "Raw",
}

function useGraphQLJSON(source: unknown) {
  return useMemo(() => {
    if (
      typeof source === "object" &&
      source &&
      "query" in source &&
      typeof source.query === "string"
    ) {
      return {
        src: source.query,
        variables: (source as { variables?: unknown }).variables ?? {},
      }
    }

    return null
  }, [source])
}

function JSONPostData({ postData }: { postData: PostData }) {
  const [tab, setTab] = useState(JSONPostDataTab.JSON)
  const [parseResult, parsed] = useMemo(() => {
    try {
      return [true, JSON5.parse(postData.text ?? "null")] as const
    } catch (e) {
      console.warn(e)
      return [false, null] as const
    }
  }, [postData])
  const graphql = useGraphQLJSON(parsed)

  useEffect(() => {
    if (!parseResult && tab !== JSONPostDataTab.Raw) {
      setTab(JSONPostDataTab.Raw)
    } else if (tab === JSONPostDataTab.GraphQL && !graphql) {
      setTab(JSONPostDataTab.JSON)
    }
  }, [parseResult, tab, graphql])

  return (
    <div css="-mt-2 mb-4" data-component="json-post-data">
      <Tabs
        classNames={{ tab: tw`px-1.5 -mx-1 h-7` }}
        color="primary"
        selectedKey={tab}
        size="md"
        variant="underlined"
        onSelectionChange={tab => setTab(tab as JSONPostDataTab)}
      >
        <Tab key={JSONPostDataTab.JSON} title={JSONPostDataTab.JSON}>
          <Card css="px-3 py-4">
            <ErrorBoundary FallbackComponent={ErrorMessage}>
              <Suspense fallback={<Loading />}>
                <JSONView minimal css="overflow-y-scroll text-sm" data={parsed} />
              </Suspense>
            </ErrorBoundary>
          </Card>
        </Tab>

        {!!graphql && (
          <Tab key={JSONPostDataTab.GraphQL} title={JSONPostDataTab.GraphQL}>
            <Card css="px-3 py-4">
              <ErrorBoundary FallbackComponent={ErrorMessage}>
                <div css="grid max-h-[75vh] grid-cols-2 gap-2 overflow-hidden">
                  <RawEditor
                    keepCurrentModel
                    readOnly
                    css="min-h-[25rem]"
                    language="graphql"
                    path="request.gql"
                    value={graphql?.src ?? ""}
                    options={{
                      lineNumbers: "off",
                    }}
                  />
                  <Suspense fallback={<Loading />}>
                    <JSONView
                      minimal
                      css="overflow-y-scroll text-sm"
                      data={graphql?.variables ?? {}}
                      name="variables"
                    />
                  </Suspense>
                </div>
              </ErrorBoundary>
            </Card>
          </Tab>
        )}

        <Tab key={JSONPostDataTab.Raw} title={JSONPostDataTab.Raw}>
          <Card css="px-3 py-4">
            <RawEditor
              keepCurrentModel
              readOnly
              css="min-h-60"
              language="json"
              path="request.json"
              value={postData.text}
              options={{
                lineNumbers: "off",
              }}
            />
          </Card>
        </Tab>
      </Tabs>
    </div>
  )
}

function FormPostData({ formData }: { formData: string }) {
  const { t } = useLingui()
  const [tab, setTab] = useState(FormPostDataTab.JSON)
  const [parseResult, parsed] = useMemo(() => {
    try {
      return [
        true,
        Array.from(new URLSearchParams(formData), ([name, value]) => ({ name, value })),
      ] as const
    } catch (e) {
      console.warn(e)
      return [false, null] as const
    }
  }, [formData])

  useEffect(() => {
    if (!parseResult && tab !== FormPostDataTab.Raw) {
      setTab(FormPostDataTab.Raw)
    }
  }, [parseResult, tab])

  return (
    <div css="mb-4 p-1" data-component="form-post-data">
      <h3 css="mb-2 ml-1 text-sm font-medium uppercase tracking-widest">
        {[FormPostDataTab.JSON, FormPostDataTab.Raw].map((item, i, { length }) => (
          <Fragment key={item}>
            {item === tab ? (
              <span css="cursor-not-allowed opacity-50">{item}</span>
            ) : (
              <Button
                css="cursor-pointer uppercase opacity-40 hover:underline"
                onPress={() => setTab(item)}
              >
                {item}
              </Button>
            )}
            {i !== length - 1 && <span css="mx-1 opacity-40">/</span>}
          </Fragment>
        ))}
      </h3>
      {tab === FormPostDataTab.JSON ? (
        <ErrorBoundary FallbackComponent={ErrorMessage}>
          <Suspense fallback={<Loading />}>
            <KVTable aria-label={t`Loading`} title={null} values={parsed!} />
          </Suspense>
        </ErrorBoundary>
      ) : (
        <Card css="px-3 py-4 shadow-small">
          <RawEditor
            keepCurrentModel
            readOnly
            css="min-h-60"
            language="json"
            path="request.json"
            value={formData}
            options={{
              lineNumbers: "off",
            }}
          />
        </Card>
      )}
    </div>
  )
}

export function PostData({ postData }: { postData?: PostData }) {
  const { t } = useLingui()
  const title = useMimeTypeLabel(postData?.mimeType)

  if (!postData) {
    return null
  } else if (title === t`JSON`) {
    return postData.text ? <JSONPostData postData={postData} /> : null
  } else if (title === t`Form data` && postData.text != null) {
    return <FormPostData formData={postData.text} />
  }

  if (!postData.params) {
    return (
      <KVBox css="mx-1" title={title || t`POST data`}>
        <div
          className={css`
            font-family: "SF Pro Rounded", var(--font-sans);
            letter-spacing: ${hasSFLetterSpacingBug ? 0.5 : 0}px;
          `}
        >
          {postData.text}
        </div>
      </KVBox>
    )
  }

  return (
    <KVTable
      sort
      aria-label={t`POST data`}
      css="mx-1 mb-6"
      title={title || t`POST data`}
      values={postData.params ?? [{ name: t`Text`, value: postData.text }]}
    />
  )
}

function useMimeTypeLabel(mimeType: string | undefined) {
  const { t } = useLingui()
  if (!mimeType) {
    return
  }

  if (mimeType.startsWith("multipart/form-data")) {
    return t`Multipart form data`
  } else if (mimeType.startsWith("application/x-www-form-urlencoded")) {
    return t`Form data`
  } else if (isJSON(mimeType)) {
    return t`JSON`
  }

  return mimeType
}
